import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useState } from "react";
import { CircleNotch, Lock, ArrowLeft, CheckCircle, Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [next, setNext] = useState("");
  const [confirmNext, setConfirmNext] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error("This reset link is missing its token. Please request a new one.");
      return;
    }
    if (next.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (next !== confirmNext) {
      toast.error("Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: next });
      setDone(true);
      toast.success("Password updated. You can now sign in with your new password.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-6 sm:p-12" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <Link
          to="/login"
          data-testid="back-to-login"
          className="inline-flex items-center gap-1 text-sm text-[#4A5568] hover:text-[#3B5BDB] smooth"
        >
          <ArrowLeft size={14} weight="bold" /> Back to sign in
        </Link>

        {!token ? (
          <div className="mt-8 mc-card p-7" data-testid="reset-missing-token">
            <h1 className="font-display text-2xl text-[#0A1128]">Link incomplete</h1>
            <p className="mt-2 text-sm text-[#4A5568]">
              This reset link doesn't have a valid token. Please request a new one from the sign-in page.
            </p>
            <Link
              to="/forgot-password"
              data-testid="request-new-link"
              className="mt-5 inline-block px-5 py-2.5 rounded-md bg-[#3B5BDB] text-white text-sm font-medium hover:bg-[#2F49AF] smooth"
            >
              Request a new link
            </Link>
          </div>
        ) : done ? (
          <div className="mt-8 mc-card p-7" data-testid="reset-success">
            <CheckCircle size={32} weight="duotone" className="text-[#16A34A]" />
            <h1 className="font-display text-3xl mt-4 text-[#0A1128]">Password updated</h1>
            <p className="mt-2 text-sm text-[#4A5568]">
              You can now sign in to Mine AI with your new password.
            </p>
            <button
              onClick={() => navigate("/login")}
              data-testid="reset-go-login"
              className="mt-5 px-5 py-2.5 rounded-md bg-[#3B5BDB] text-white font-medium hover:bg-[#2F49AF] smooth"
            >
              Sign in
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8" data-testid="reset-form">
            <div className="flex items-center gap-2">
              <Lock size={22} weight="duotone" className="text-[#3B5BDB]" />
              <h1 className="font-display text-3xl sm:text-4xl text-[#0A1128]">Choose a new password</h1>
            </div>
            <p className="mt-2 text-sm text-[#4A5568]">
              Pick something unique and at least 8 characters long.
            </p>
            <div className="mt-7 space-y-4">
              <div className="relative">
                <label className="text-xs uppercase tracking-wider text-[#4A5568]">New password</label>
                <input
                  type={show ? "text" : "password"}
                  data-testid="reset-new-password"
                  value={next}
                  onChange={(e) => setNext(e.target.value)}
                  autoFocus
                  required
                  minLength={8}
                  autoComplete="new-password"
                  className="mt-1 w-full h-11 px-3 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB] focus:ring-1 focus:ring-[#3B5BDB] smooth"
                />
                <button
                  type="button"
                  onClick={() => setShow(!show)}
                  className="absolute right-2 top-7 p-2 text-[#4A5568] hover:text-[#0A1128]"
                  aria-label={show ? "Hide passwords" : "Show passwords"}
                  data-testid="reset-toggle-visibility"
                >
                  {show ? <EyeSlash size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#4A5568]">Confirm new password</label>
                <input
                  type={show ? "text" : "password"}
                  data-testid="reset-confirm-password"
                  value={confirmNext}
                  onChange={(e) => setConfirmNext(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  className="mt-1 w-full h-11 px-3 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB] focus:ring-1 focus:ring-[#3B5BDB] smooth"
                />
              </div>
              <button
                type="submit"
                data-testid="reset-submit"
                disabled={loading || !next || !confirmNext}
                className="w-full h-11 rounded-md bg-[#3B5BDB] text-white font-medium hover:bg-[#2F49AF] smooth disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {loading ? <CircleNotch size={16} className="animate-spin" weight="bold" /> : null}
                {loading ? "Updating..." : "Update password"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
